import { Injectable } from '@angular/core';
import { AppConfig } from '../app.config';
import { AppGlobals } from '../app.globals';
import { HttpClient, HttpContext, HttpHeaders, HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ApiBaseService {
  constructor(
    private appGlobals: AppGlobals,
    private http: HttpClient
  ) {}

  async httpRequest(apiPath: string, postBody: any, authenticatedRequest: boolean, showLoader: boolean): Promise<any> {
    let requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;
    postBody.Language = AppGlobals.userDefinedLanguage;
    postBody.BaseUrl = AppGlobals.BaseUrl;
    const requestHeaders: HttpHeaders = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      SignInEnvironment: AppConfig.AppEnvironment,
      WebAppVersion: AppConfig.APPVersion,
      'X-TenantId': AppGlobals.TenantId,
    });

    if (authenticatedRequest === true) {
      //requestHeaders.Authorization = "Bearer " + AppGlobals.UserToken;
      /*if (window.localStorage.getItem(AppConfig.AuthTokenLocation)) {
        requestHeaders.wienergyPlatformToken = window.localStorage.getItem(AppConfig.AuthTokenLocation)
      }*/
    }

    if (showLoader === true) {
      this.appGlobals.ShowGlobalLoader();
    }

    let that = this;

    return new Promise(async function (resolve, reject) {
      let serverResponse = that.http.post(requestURL, JSON.stringify(postBody), { headers: requestHeaders }).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: (err: any) => {
          // RxJS never fires complete() after an error, so without this the global loader stays
          // up forever on any failed request whose caller swallows the rejection (a .catch() that
          // doesn't go through AppGlobals.ShowErrorMessage, which clears the flag as a side effect).
          // Guarded on showLoader, matching ShowGlobalLoader() above: an unconditional hide here
          // would drop a concurrent showLoader:true request's spinner out from under it.
          if (showLoader === true) that.appGlobals.HideGlobalLoader();
          reject(err);
        },
        // Only a request that showed the loader hides it; one without (e.g. the contact search while
        // typing) would otherwise take away the spinner of a request still running.
        complete: () => {
          if (showLoader === true) that.appGlobals.HideGlobalLoader();
        },
      });
    });
  }
  async httpGETRequest(
    apiPath: string,
    params: any,
    showLoader: boolean,
    responseType: 'json' | 'blob' = 'json' // Add responseType parameter
  ): Promise<any> {
    let requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;
    const requestHeaders: HttpHeaders = new HttpHeaders({
      Accept: responseType === 'blob' ? '*/*' : 'application/json',
      SignInEnvironment: AppConfig.AppEnvironment,
      WebAppVersion: AppConfig.APPVersion,
      'X-TenantId': AppGlobals.TenantId,
    });

    params.BaseUrl = AppGlobals.BaseUrl;

    if (showLoader === true) {
      this.appGlobals.ShowGlobalLoader();
    }

    let that = this;
    return new Promise(async function (resolve, reject) {
      const requestOptions: any = {
        headers: requestHeaders,
        params: params,
      };

      // Add responseType for blob requests
      if (responseType === 'blob') {
        requestOptions.responseType = 'blob';
      }

      that.http.get(requestURL, requestOptions).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: (err: any) => {
          // RxJS never fires complete() after an error, so without this the global loader stays
          // up forever on any failed request whose caller swallows the rejection (a .catch() that
          // doesn't go through AppGlobals.ShowErrorMessage, which clears the flag as a side effect).
          // Guarded on showLoader, matching ShowGlobalLoader() above: an unconditional hide here
          // would drop a concurrent showLoader:true request's spinner out from under it.
          if (showLoader === true) that.appGlobals.HideGlobalLoader();
          reject(err);
        },
        // Only a request that showed the loader hides it; one without (e.g. the contact search while
        // typing) would otherwise take away the spinner of a request still running.
        complete: () => {
          if (showLoader === true) that.appGlobals.HideGlobalLoader();
        },
      });
    });
  }

  async fetchRequest(apiPath: string, postBody: any, authenticatedRequest: boolean, showLoader: boolean): Promise<any> {
    let requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;

    let requestHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      SignInEnvironment: AppConfig.AppEnvironment,
      WebAppVersion: AppConfig.APPVersion,
      'X-TenantId': AppGlobals.TenantId,
    };

    if (showLoader === true) {
      this.appGlobals.ShowGlobalLoader();
    }

    let that = this;

    postBody.BaseUrl = AppGlobals.BaseUrl;

    return new Promise(async function (response, error) {
      let connectionSuccess = true;

      let serverResponse = await fetch(requestURL, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(postBody),
      }).catch(errorMsg => {
        connectionSuccess = false;
        error(errorMsg);
      });
      that.appGlobals.HideGlobalLoader();

      if (connectionSuccess === true) {
        if (serverResponse) {
          if (serverResponse.ok) {
            let json = await serverResponse.json();
            response(json);
          } else {
            error(serverResponse);
          }
        } else {
          error('no response');
        }
      }
    });
  }

  /** POST with a JSON body that returns binary (blob) data rather than JSON. */
  async httpPostBlobRequest(apiPath: string, postBody: any, showLoader: boolean): Promise<Blob> {
    const requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;
    postBody.Language = AppGlobals.userDefinedLanguage;
    postBody.BaseUrl = AppGlobals.BaseUrl;
    const headers = new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'application/json',
      SignInEnvironment: AppConfig.AppEnvironment,
      WebAppVersion: AppConfig.APPVersion,
      'X-TenantId': AppGlobals.TenantId,
    });
    if (showLoader) this.appGlobals.ShowGlobalLoader();
    const that = this;
    return new Promise((resolve, reject) => {
      that.http.post(requestURL, JSON.stringify(postBody), { headers, responseType: 'blob' }).subscribe({
        next: (blob: Blob) => resolve(blob),
        error: (err: any) => {
          // see httpRequest: complete() never fires after error; guarded on showLoader for the same reason.
          if (showLoader) that.appGlobals.HideGlobalLoader();
          reject(err);
        },
        complete: () => that.appGlobals.HideGlobalLoader(),
      });
    });
  }

  /**
   * As httpPostBlobRequest, but also returns the server-supplied download filename from
   * Content-Disposition. Requires the API's CORS policy to expose that header.
   */
  async httpPostBlobFileRequest(
    apiPath: string,
    postBody: any,
    showLoader: boolean
  ): Promise<{ blob: Blob; fileName: string }> {
    const requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;
    postBody.Language = AppGlobals.userDefinedLanguage;
    postBody.BaseUrl = AppGlobals.BaseUrl;
    const headers = new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'application/json',
      SignInEnvironment: AppConfig.AppEnvironment,
      WebAppVersion: AppConfig.APPVersion,
      'X-TenantId': AppGlobals.TenantId,
    });
    if (showLoader) this.appGlobals.ShowGlobalLoader();
    const that = this;
    return new Promise((resolve, reject) => {
      that.http
        .post(requestURL, JSON.stringify(postBody), { headers, responseType: 'blob', observe: 'response' })
        .subscribe({
          next: (response: HttpResponse<Blob>) =>
            resolve({
              blob: response.body,
              fileName: that.parseContentDispositionFileName(response.headers.get('Content-Disposition')),
            }),
          error: (err: any) => {
            // see httpRequest: complete() never fires after error; guarded on showLoader for the same reason.
            if (showLoader) that.appGlobals.HideGlobalLoader();
            reject(err);
          },
          complete: () => that.appGlobals.HideGlobalLoader(),
        });
    });
  }

  /** GET that returns binary data plus the server-supplied download filename from Content-Disposition. */
  async httpGETBlobFileRequest(apiPath: string, params: any, showLoader: boolean): Promise<{ blob: Blob; fileName: string }> {
    const requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;
    const headers = new HttpHeaders({
      Accept: '*/*',
      SignInEnvironment: AppConfig.AppEnvironment,
      WebAppVersion: AppConfig.APPVersion,
      'X-TenantId': AppGlobals.TenantId,
    });
    params.BaseUrl = AppGlobals.BaseUrl;
    if (showLoader) this.appGlobals.ShowGlobalLoader();
    const that = this;
    return new Promise((resolve, reject) => {
      that.http.get(requestURL, { headers, params, responseType: 'blob', observe: 'response' }).subscribe({
        next: (response: HttpResponse<Blob>) =>
          resolve({
            blob: response.body,
            fileName: that.parseContentDispositionFileName(response.headers.get('Content-Disposition')),
          }),
        error: (err: any) => {
          // see httpRequest: complete() never fires after error; guarded on showLoader for the same reason.
          if (showLoader) that.appGlobals.HideGlobalLoader();
          reject(err);
        },
        complete: () => that.appGlobals.HideGlobalLoader(),
      });
    });
  }

  private parseContentDispositionFileName(contentDisposition: string | null): string {
    if (!contentDisposition) return '';

    // filename* (RFC 5987) is preferred — ASCII filename= is a lossy fallback for non-ASCII names.
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (encoded) return decodeURIComponent(encoded[1].trim());

    const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return plain ? plain[1].trim() : '';
  }

  async httpRequestForm(
    apiPath: string,
    postBody: any,
    authenticatedRequest: boolean,
    showLoader: boolean
  ): Promise<any> {
    let requestURL = `${AppConfig.APIEndPointBase}/${apiPath}`;

    let requestHeaders: { [header: string]: string } = {
      'X-TenantId': AppGlobals.TenantId,
    };

    // Do not set 'Content-Type' here. FormData will handle it for you.
    // If you're not sending FormData, the default Content-Type (usually application/json) is appropriate.

    if (showLoader === true) {
      this.appGlobals.ShowGlobalLoader();
    }

    let that = this;

    return new Promise((response, error) => {
      let requestPayload: any = postBody;
      if (!(postBody instanceof FormData)) {
        postBody.BaseUrl = AppGlobals.BaseUrl;
        requestPayload = JSON.stringify(postBody);
      }

      that.http.post(requestURL, requestPayload, { headers: requestHeaders }).subscribe({
        next: (res: any) => {
          response(res);
        },
        error: (err: any) => {
          console.error(err);
          // see httpRequest: complete() never fires after error; guarded on showLoader for the same reason.
          if (showLoader) that.appGlobals.HideGlobalLoader();
          AppGlobals.ShowErrorMessage(err);
          error(err);
        },
        // Only a request that showed the loader hides it; one without (e.g. the contact search while
        // typing) would otherwise take away the spinner of a request still running.
        complete: () => {
          if (showLoader === true) that.appGlobals.HideGlobalLoader();
        },
      });
    });
  }
}
